import React from 'react';

import ProdoqRolePage from './pages/Prodoq/Role';
import { GlobalStyles } from './shared/styles/GlobalStyles';
import { ThemeProvider } from 'styled-components';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import Theme from './shared/styles/Theme';
import 'dayjs/locale/ko';
import '@mantine/notifications/styles.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';

// 페이지 import
import Layout from './components/ui/layout/Layout';
import NotFound from './components/ui/err/NotFound';
import ErrorBoundary from './components/ui/err/ErrorBoundary';

// 토큰 payload에 담겨오는 정보를 바탕으로 로그인 라우팅 처리
import jwtDecode from 'jwt-decode';
import { getCookie } from './shared/cookie';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';

import { mantineTheme } from './shared/styles/mantineTheme';
import DispatchEpTpPage from './pages/Management/Dispatch/EpTp';
import DispatchEndUserPage from './pages/Management/Dispatch/EndUser';
import DispatchManufacturerPage from './pages/Management/Dispatch/Manufacturer';
import DispatchEpTpRegisterPage from './pages/Management/Dispatch/EpTp/Register';
import DispatchManufacturerRegisterPage from './pages/Management/Dispatch/Manufacturer/Register';
import DeviceRegisterPage from './pages/Management/Device/register';
import DeviceListPage from './pages/Management/Device/list';
import CompanyListPage from './pages/Management/Company/list';
import LoginPage from './pages/Login';
import CompanyDetailsPage from './pages/Management/Company/details';
import SignUpPage from './pages/Signup';
import DataPointMembershipPage from './pages/DataPoint/Membership';
import DataPointNotFoundPage from './pages/DataPoint/NotFound';
import DataPointPaymentPage from './pages/DataPoint/Payment';
import DataPointRefundHistoryPage from './pages/DataPoint/Refund/History';
import BrochureRequestPage from './pages/Edgecross/Brochure';
import NoticeDetailsPage from './pages/Edgecross/Notice/Details';
import NoticeRegisterPage from './pages/Edgecross/Notice/Register';
import ProgixPlanPage from './pages/Progix/Plan';
import ServiceManagementPage from './pages/Progix/Service';
import ProgixRefundHistoryPage from './pages/Progix/Refund/History';
import ProgixRefundPage from './pages/Progix/Refund';
import ProgixPaymentPage from './pages/Progix/Payment';
import DataPointRefundPage from './pages/DataPoint/Refund';
import AnnouncementPage from './pages/Edgecross/Notice/List';
import PopupRegisterPage from './pages/Edgecross/Notice/Popup/Register';
import CustomCssVariables from './shared/styles/variables';
import AiAgentPage from './pages/Management/AiAgent';
import TemplateRegisterPage from './pages/Management/AiAgent/template/register';
import DeviceDetailsPage from './pages/Management/Device/details';
import ModemRegisterPage from './pages/Management/Modem/register';
import ModemListPage from './pages/Management/Modem/list';
import ModemDetailsPage from './pages/Management/Modem/details';
import AiAgentServiceOperationSettingHistoryPage from './pages/Management/AiAgent/operation/history';
import AiAgentModelPage from './pages/Management/AiAgent/model';
import AiAgentManualPage from './pages/Management/AiAgent/manual';
import AiAgentTemplatePage from './pages/Management/AiAgent/template';
import AiAgentAsCsPage from './pages/Management/AiAgent/asCs';
import AiAgentManualUploadStatus from './pages/Management/AiAgent/manual/status';
import AiAgentModelHistoryPage from './pages/Management/AiAgent/model/history';
import AiAgentManualHistoryPage from './pages/Management/AiAgent/manual/history';
import AiAgentAsCsDetailsPage from './pages/Management/AiAgent/asCs/details';
import AiAgentAsCsFileUploadStatusPage from './pages/Management/AiAgent/asCs/file/status';
import AiAgentAsCsFileListPage from './pages/Management/AiAgent/asCs/file';

function App() {
  // 토큰 타입정의
  interface TokenProps {
    exp: number;
    iat: number;
    authority: string;
    solution: string[];
    sub: string;
  }

  const accessToken = getCookie('Authorization');
  let authority: TokenProps;
  let isAuth: boolean | undefined;

  if (accessToken) {
    authority = jwtDecode(accessToken);
    isAuth = authority.authority === 'ROLE_ADMIN';
  }

  return (
    <React.Fragment>
      <MantineProvider theme={mantineTheme} cssVariablesResolver={CustomCssVariables}>
        <ThemeProvider theme={Theme}>
          <DatesProvider settings={{ locale: 'ko' }}>
            <ModalsProvider>
              <Notifications />
              <GlobalStyles />
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={isAuth ? <Navigate to="/admin/management/device" /> : <LoginPage />} />
                  <Route path="/signup" element={isAuth ? <Navigate to="/admin/management/device" /> : <SignUpPage />} />
                  <Route path="/admin" element={isAuth ? <Layout /> : <Navigate to="/" />}>
                    <Route path="progix" element={<Outlet />}>
                      <Route path="payment" element={<ProgixPaymentPage />} />
                      <Route path="refund" element={<ProgixRefundPage />} />
                      <Route path="refund-success" element={<ProgixRefundHistoryPage />} />
                      <Route path="deviceService" element={<ServiceManagementPage />} />
                    </Route>

                    <Route path="prodoq" element={<Outlet />}>
                      <Route path="role" element={<ProdoqRolePage />} />
                    </Route>

                    <Route path="datapoint" element={<Outlet />}>
                      <Route path="member" element={<DataPointMembershipPage />} />
                      <Route path=":userId" element={<DataPointNotFoundPage />} />
                      <Route path="payment" element={<DataPointPaymentPage />} />
                      <Route path="refund" element={<DataPointRefundPage />} />
                      <Route path="refund-success" element={<DataPointRefundHistoryPage />} />
                    </Route>

                    <Route path="management" element={<Outlet />}>
                      <Route path="company" element={<Outlet />}>
                        <Route index element={<CompanyListPage />} />
                        <Route path=":companyId" element={<CompanyDetailsPage />} />
                      </Route>
                      <Route path="modem" element={<Outlet />}>
                        <Route index element={<ModemListPage />} />
                        <Route path="register" element={<ModemRegisterPage />} />
                        <Route path="register/:id" element={<ModemRegisterPage />} />
                        <Route path="details/:id" element={<ModemDetailsPage />} />
                      </Route>
                      <Route path="device" element={<Outlet />}>
                        <Route index element={<DeviceListPage />} />
                        <Route path=":deviceId" element={<DeviceDetailsPage />} />
                        <Route path="register" element={<DeviceRegisterPage />} />
                        <Route path="register/:id" element={<DeviceRegisterPage />} />
                        <Route path="details/:id" element={<DeviceDetailsPage />} />
                      </Route>
                      <Route path="aiagent/company" element={<Outlet />}>
                        <Route index element={<AiAgentPage />} />
                        <Route path=":companyId/operation/history" element={<AiAgentServiceOperationSettingHistoryPage />} />
                        <Route path=":companyId/model" element={<AiAgentModelPage />} />
                        <Route path=":companyId/model/:modelId/history" element={<AiAgentModelHistoryPage />} />
                        <Route path=":companyId/manual" element={<AiAgentManualPage />} />
                        <Route path=":companyId/manual/:fileUploadId/status" element={<AiAgentManualUploadStatus />} />
                        <Route path=":companyId/manual/:fileUploadId/history" element={<AiAgentManualHistoryPage />} />
                        <Route path=":companyId/template" element={<AiAgentTemplatePage />} />
                        <Route path=":companyId/template/register" element={<TemplateRegisterPage />} />
                        <Route path=":companyId/template/register/:templateId" element={<TemplateRegisterPage />} />
                        <Route path=":companyId/ascs" element={<AiAgentAsCsPage />} />
                        <Route path=":companyId/ascs/:ascsId" element={<AiAgentAsCsDetailsPage />} />
                        <Route path=":companyId/ascs/file" element={<AiAgentAsCsFileListPage />} />
                        <Route path=":companyId/ascs/file/:fileUploadId/status" element={<AiAgentAsCsFileUploadStatusPage />} />
                      </Route>
                      <Route path="dispatch" element={<Outlet />}>
                        <Route path="eptp" element={<DispatchEpTpPage />} />
                        <Route path="eptp/register" element={<DispatchEpTpRegisterPage />} />
                        <Route path="manufacturer" element={<DispatchManufacturerPage />} />
                        <Route path="manufacturer/register" element={<DispatchManufacturerRegisterPage />} />
                        <Route path="end-user" element={<DispatchEndUserPage />} />
                      </Route>
                      <Route path="plan" element={<ProgixPlanPage />} />
                    </Route>

                    <Route path="edgecross" element={<Outlet />}>
                      <Route path="notice" element={<AnnouncementPage />} />
                      <Route path="notice/:id" element={<NoticeDetailsPage />} />
                      <Route path="notice/write" element={<NoticeRegisterPage />} />
                      <Route path="brochure" element={<BrochureRequestPage />} />
                      <Route path="notice/popup/register" element={<PopupRegisterPage />} />
                      <Route path="notice/popup/register/:id" element={<PopupRegisterPage />} />
                    </Route>
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ErrorBoundary>
            </ModalsProvider>
          </DatesProvider>
        </ThemeProvider>
      </MantineProvider>
    </React.Fragment>
  );
}

export default App;
